import React, { useCallback, useEffect, useState } from "react";
import { shallowEqual, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  useAssessFulfillScoreMutation,
  useDeleteFulfillMutation,
  useDownloadFulfillImagesMutation,
  useFulfillDetailQuery,
  useModifyFulfillMutation,
  useNextFulfillLinkQuery,
} from "src/api/fulfillAPI";
import { useAppSelector } from "src/store/actions/action";
import { getPathValue } from "src/utils/queryStringUtil";
import moment from "moment";
import Body from "src/components/common/Body";
import Button from "src/components/common/Button";
import IconButton from "src/components/common/IconButton";
import Slider from "react-slick";
import List from "src/components/common/List";
import VideoBox from "src/components/common/VideoBox";
import CommentInput from "src/components/notice/FulfillCommentInput";
import CommentItem from "src/components/notice/FulfillCommentItem";
import RedeemListItem from "src/components/notice/RedeemListItem";
import ScoreComment from "src/components/notice/ScoreComment";
import ScoreRegisterBox from "src/components/notice/ScoreRegisterBox";
import ImageModal, { RefProps } from "src/components/common/ImageModal";
import BottomModal from "src/components/common/BottomModal";
import SVG from "src/utils/SVG";
import styled, { css } from "styled-components";
import { pcDetailSliderArrowStyles, pcModalSliderStyles, pcModalBackgroundStyles } from "src/styles/sliderStyles";
import FulfillProcessItem from "src/components/filter/FulfillProcessItem";
import { setRedeemDataAllInit } from "../../store/reducers/redeemReduce";
import FulFillCommentInputV2 from "../../components/notice/FulfillCommentInputV2";
import { setOnFocusReduce, setWriteNameReduce } from "../../store/reducers/fulfillDetailReduce";

function fulfillDetail() {
  // 글로벌
  const navigate = useNavigate();
  const nxId: any = getPathValue(); // 이행번호
  const imageServerIp = process.env.REACT_APP_IMAGE_SERVER_IP;
  const userInfo = useAppSelector((state) => state.userReduce.userInfo, shallowEqual);
  const assessData = useAppSelector((state) => state.fulfillDetailReduce.assessData);

  const [slideIndex, setSlideIndex] = useState(0);
  const [saveImgUrl, setSaveImgUrl] = useState<any>();
  const [redeemCheck, setRedeemCheck] = useState(false);
  const [noticeIdx, setNoticeIdx] = useState("");

  // PC/모바일 반응형 상태
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth <= 768;
    }
    return true;
  });

  // PC/모바일 반응형 감지
  const checkMobile = useCallback(() => {
    setIsMobile(window.innerWidth <= 768);
  }, []);

  useEffect(() => {
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, [checkMobile]);

  // API 호출 영역
  const fulfillDetail = useFulfillDetailQuery(nxId, { skip: nxId === undefined });
  const nextFulfillLink = useNextFulfillLinkQuery({ notice_idx: noticeIdx, execute_idx: nxId }, { skip: noticeIdx === "" || nxId === "" });
  const [insertAssess, { isLoading: isUpdating, error: setError, isSuccess: insertSuccess }] = useAssessFulfillScoreMutation();

  // AS 취소
  const [fulfillModify, { isLoading: isUpdatingModify, isSuccess: isSuccessModify, isError: isErrorModify, error: modifyError }] = useModifyFulfillMutation();
  // AS 삭제
  const [fufillDelete, { isLoading: isUpdatingDelete, isSuccess: isSuccessDelete, isError: isErrorDelete, error: deleteError }] = useDeleteFulfillMutation();
  // 이행 사진 전체 다운로드 (zip)
  const [downloadFulfillImages, { isLoading: isDownloadingAll }] = useDownloadFulfillImagesMutation();

  const dispatch = useDispatch();

  useEffect(() => {
    if (fulfillDetail.isSuccess && fulfillDetail.data.images.length > 0) {
      // 데이터 가져올때 첫 이미지 다운로드 url 세팅
      getImageDownUrl(0);

      // 재이행 통과 여부
      const isPassed = (v: any) => v.type === "C" || v.type === null;
      const allPassed = fulfillDetail.data.images.every(isPassed);
      setRedeemCheck(allPassed);

      // 다음 이행글
      setNoticeIdx(fulfillDetail.data.notice_idx);
    }
  }, [fulfillDetail]);

  // 헤더 메뉴더보기
  const processModalRef = React.useRef<RefProps>(null);

  const openProcessModal = () => {
    if (processModalRef.current) processModalRef.current.modalOpen();
  };

  const fulfillProcessBtn = (code: string) => {
    if (code === "U") {
      // 수정하기
      const data = {
        images: "",
        execute_contents: "",
      };
      navigate("/fulfill-Register/" + fulfillDetail.data.notice_idx, { state: { modify: true, fulfillId: fulfillDetail.data.execute_idx } });
    } else if (code === "D") {
      // 삭제하기
      if (confirm("등록된 이행글을 삭제하시겠습니까?")) {
        fufillDelete(fulfillDetail.data.notice_idx).then(() => {
          navigate("/notice-detail/" + fulfillDetail.data.notice_idx, { replace: true });
        });
      }
    }
  };

  // slide 바뀔때마다 이미지 다운로드 url 세팅
  useEffect(() => {
    getImageDownUrl(slideIndex);
  }, [slideIndex]);

  const [nav1, setNav1] = useState();
  const [nav2, setNav2] = useState();
  const slider1 = React.useRef(null);
  const slider2 = React.useRef(null);

  useEffect(() => {
    if (slider1.current !== null && slider2.current !== null) {
      setNav1(slider1.current);
      setNav2(slider2.current);
    }
  }, [fulfillDetail.isSuccess]);

  // 이미지 슬라이드 옵션
  const setImgSlide1 = {
    ref: slider1,
    asNavFor: nav2,
    useTransform: false,
    dots: true,
    infinite: false,
    fade: true,
    speed: 100,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: !isMobile,
    cssEase: "ease-out",
    beforeChange: function (oldIdx: number, newIdx: number) {
      setSlideIndex(newIdx === -1 ? 0 : newIdx);
    },
  };

  const setImgSlide2 = {
    ref: slider2,
    asNavFor: nav1,
    useTransform: false,
    dots: true,
    infinite: false,
    speed: 100,
    fade: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: !isMobile,
    cssEase: "ease-out",
  };

  const settings = {
    useTransform: false,
    dots: true,
    infinite: false,
    speed: 100,
    fade: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    cssEase: "ease-out",
  };

  // 이미지 모달
  const imageModalRef = React.useRef<RefProps>(null);
  const openImageModal = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (imageModalRef.current) imageModalRef.current.modalOpen();
  };

  const cmtImgModalRef = React.useRef<RefProps>(null);
  const [cmtView, setCmtView] = useState({
    cmtType: "parent",
    cmtIdx: 0,
    cmtchildIdx: 0,
    imgIdx: 0,
  });
  const [imageSlider, setImageSlider] = useState<Slider | null>(null);
  const [changeSlide, setChangeSlide] = useState(false);

  const clickCmtImages = (e: React.MouseEvent<HTMLLIElement>) => {
    setCmtView({
      ...cmtView,
      cmtType: String(e.currentTarget.dataset.cmtType),
      cmtIdx: Number(e.currentTarget.dataset.cmtIdx),
      cmtchildIdx: Number(e.currentTarget.dataset.cmtchildIdx),
      imgIdx: Number(e.currentTarget.dataset.imgIdx),
    });
    imageSlider?.slickGoTo(Number(e.currentTarget.dataset.imgIdx));
    setChangeSlide(true);
  };

  useEffect(() => {
    if (changeSlide) {
      if (cmtImgModalRef.current) cmtImgModalRef.current.modalOpen();
    }
    setChangeSlide(false);
  }, [changeSlide]);

  // 이미지다운로드 구현
  const getImageDownUrl = (slideIndex: number) => {
    if (fulfillDetail.isSuccess) {
      const url = imageServerIp + fulfillDetail.data.images[slideIndex].img_url;
      setSaveImgUrl(url);
    }
  };

  // 이행 사진 다운로드 선택 레이어 (현재 사진 / 전체)
  const downloadModalRef = React.useRef<RefProps>(null);
  const openDownloadModal = () => {
    // 사진이 1장뿐이면 선택 레이어 없이 바로 그 사진을 다운로드
    if (fulfillDetail.isSuccess && fulfillDetail.data.images.length <= 1) {
      downloadCurrentImage();
      return;
    }
    if (downloadModalRef.current) downloadModalRef.current.modalOpen();
  };

  // 현재 보고 있는 사진 한 장 다운로드 (기존 방식 유지 - S3 key 직접)
  const downloadCurrentImage = () => {
    if (!saveImgUrl) return;
    const link = document.createElement("a");
    link.href = saveImgUrl;
    link.download = "";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    downloadModalRef.current?.modalClose();
  };

  // 전체 다운로드 (zip) - 매장(이행) 단위 전체 사진 일괄 다운로드
  const downloadAllImages = async () => {
    if (isDownloadingAll) return;
    try {
      await downloadFulfillImages(nxId).unwrap();
      downloadModalRef.current?.modalClose();
    } catch (e) {
      alert("이미지 다운로드에 실패했습니다. 잠시 후 다시 시도해주세요.");
    }
  };

  // 재이행페이지 이동
  const linkToRedeem = () => {
    dispatch(setRedeemDataAllInit());
    navigate("/redeem-select", { state: { images: fulfillDetail.data.images, fulfillId: fulfillDetail.data.execute_idx } });
  };

  // 별점 모달
  const scoreModalRef = React.useRef<RefProps>(null);

  const openScoreModal = () => {
    if (scoreModalRef.current) scoreModalRef.current.modalOpen();
  };

  // 별점 등록
  const setAssessScore = async () => {
    let confirmedMes = false;

    const data = {
      execute_idx: nxId,
      score: assessData.score,
      assess_contents: assessData.comment,
    };

    if (assessData.comment === "" && assessData.score === 0) {
      alert("별점 0점일 경우 코멘트 작성은 필수입니다.");
      confirmedMes = false;
      return;
    } else {
      confirmedMes = true;
    }

    // 등록 했을때 api post 보내기
    if (confirmedMes)
      await insertAssess(data).then(() => {
        nextFulfillLink.refetch();
      });

    if (scoreModalRef.current) scoreModalRef.current.modalClose();
  };

  useEffect(() => {
    if (insertSuccess) {
      fulfillDetail.refetch();
    }
  }, [insertSuccess]);

  function moveToNextFulfill() {
    if (nextFulfillLink.data) {
      if (nextFulfillLink.data.execute_idx === "") {
        alert("모든 이행건의 평가를 완료했습니다.");
      } else {
        navigate("/fulfill-detail/" + nextFulfillLink.data.execute_idx);
      }
    }
  }

  useEffect(() => {
    dispatch(setOnFocusReduce(false));
    dispatch(setWriteNameReduce(["", ""]));
  }, []);

  return (
    <>
      <header>
        <div>
          <Button variant="text" className="btn-back" onClick={() => navigate(-1)}>
            {fulfillDetail.isSuccess && fulfillDetail.data.store_name}
          </Button>
        </div>
        <div className="btn-box">
          <IconButton btnName="프로필페이지" className="information" onClick={() => navigate("/profile/" + fulfillDetail.data.store_id)}>
            <SVG name="information" />
          </IconButton>
          {/* 본사기능은 2.0 이후 VMD 픽 추가 예정 */}
          {/* 매장이면서 본인 글만 수정 노출 */}
          {fulfillDetail.isSuccess && (
            <>
              {userInfo.user_type === "S" && fulfillDetail.data.store_name === userInfo.name && (
                <>
                  {fulfillDetail.data.assess_yn === "N" && fulfillDetail.data.re_execute_yn === "N" && (
                    <IconButton btnName="메뉴더보기" className="more" onClick={openProcessModal}>
                      <SVG name="more" />
                    </IconButton>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </header>

      <Body type="detail">
        <ResponsiveContainer $isMobile={isMobile}>
          {fulfillDetail.isSuccess && (
            <>
              {/* PC: 2컬럼 레이아웃 */}
              <MainContentArea $isMobile={isMobile}>
                {/* 이미지 슬라이더 영역 */}
                <ImageSection $isMobile={isMobile}>
                  {fulfillDetail.data.images.length > 0 && (
                    <ImgSlide $isMobile={isMobile}>
                      <Slider {...setImgSlide1}>
                        {fulfillDetail.data.images.map((v: any, i: number) => {
                          return (
                            <div onClick={openImageModal} key={i}>
                              <div className="img-box detail-slide">
                                {v.ext === "mp4" || v.ext === "mov" ? (
                                  <VideoBox controls vimeoId={v.img_url} />
                                ) : (
                                  <img src={imageServerIp + v.img_url} alt="더미이미지" />
                                )}
                                {v.type !== null && <span className="tag-redeem">재이행</span>}
                              </div>
                            </div>
                          );
                        })}
                      </Slider>

                      {/* 다운로드 아이콘 클릭 시 '현재 사진 / 전체' 선택 레이어 오픈 */}
                      {userInfo.user_type === "E" && (
                        <button type="button" className="img-download" onClick={openDownloadModal}>
                          <span className="hidden">이미지다운로드</span>
                          <SVG name="imgDownload" width={20} height={20} viewBox="0 0 20 20" />
                        </button>
                      )}
                    </ImgSlide>
                  )}
                </ImageSection>

                {/* 상세 정보 영역 */}
                <InfoSection $isMobile={isMobile}>
                  <EditorContentsBox $isMobile={isMobile}>
                    <div>{fulfillDetail.data.execute_contents ? fulfillDetail.data.execute_contents : "작성된 글이 없습니다."}</div>
                    <p className="date">{moment(fulfillDetail.data.insert_date).format("YYYY.MM.DD HH:mm")}</p>
                  </EditorContentsBox>

                  {/* 이행 완료시 노출 : 이행 Y, 재이행 N, 평가 N */}
                  {userInfo.user_type === "E" &&
                    fulfillDetail.data.execute_yn === "Y" &&
                    fulfillDetail.data.re_execute_yn === "N" &&
                    fulfillDetail.data.assess_yn === "N" && (
                      <ButtonBox $isMobile={isMobile} className="assess-btn-box">
                        <Button variant="outlined" br="round" color="secondary" onClick={linkToRedeem}>
                          재이행
                        </Button>
                        <Button variant="contained" br="round" onClick={openScoreModal}>
                          평가
                        </Button>
                      </ButtonBox>
                    )}

                  {/* 재이행 이력이 있는 경우 노출 : 이행 N, 재이행 Y  */}
                  {fulfillDetail.data.re_execute_yn === "Y" && (
                    <RedeemBox $isMobile={isMobile}>
                      <div>
                        <p className="title">재이행 대상</p>
                        <p className="num">총 {fulfillDetail.data.images.filter((e: any) => e.type !== null).length}건</p>
                      </div>
                      <ul>
                        <RedeemListItem data={fulfillDetail.data.images} />
                      </ul>
                    </RedeemBox>
                  )}

                  {/* 재이행 이력이 있고 재이행 모두 확인 완료시(이미지type 확인 - 전부 C) 노출 */}
                  {userInfo.user_type === "E" && fulfillDetail.data.re_execute_yn === "Y" && fulfillDetail.data.assess_yn === "N" && redeemCheck && (
                    <ButtonBox $isMobile={isMobile}>
                      <Button variant="contained" br="round" onClick={openScoreModal}>
                        평가
                      </Button>
                    </ButtonBox>
                  )}

                  {/* 본사가 이행 확인전 매장에 노출 */}
                  {userInfo.user_type !== "E" &&
                    fulfillDetail.data.execute_yn === "Y" &&
                    fulfillDetail.data.re_execute_yn === "N" &&
                    fulfillDetail.data.assess_yn === "N" && (
                      <BeforeConfirm $isMobile={isMobile}>
                        <p>본사 확인 전입니다.</p>
                      </BeforeConfirm>
                    )}

                  {/* 본사가 이행 평가전 매장에 노출 */}
                  {userInfo.user_type !== "E" &&
                    fulfillDetail.data.execute_yn === "Y" &&
                    fulfillDetail.data.re_execute_yn === "Y" &&
                    fulfillDetail.data.assess_yn === "N" &&
                    fulfillDetail.data.score === null && (
                      <BeforeAssess $isMobile={isMobile}>
                        <p>본사 평가 전입니다.</p>
                      </BeforeAssess>
                    )}

                  {/* 평가 완료시 노출 : 별점 평가 내용 및 버튼영역(재평가, 다음 평가) */}
                  {fulfillDetail.data.assess_yn === "Y" && (
                    <StartCmtBox $isMobile={isMobile}>
                      <p className="title">별점평가</p>
                      <ScoreComment
                        score={fulfillDetail.data.score}
                        date={fulfillDetail.data.assess_date}
                        contents={fulfillDetail.data.assess_contents}
                        assessor={fulfillDetail.data.assessor_name}
                      />
                    </StartCmtBox>
                  )}

                  {/* 별점 평가 후 노출 */}
                  {userInfo.user_type === "E" && fulfillDetail.data.score !== null && (
                    <ButtonBox $isMobile={isMobile}>
                      <Button variant="outlined" br="round" color="secondary" onClick={openScoreModal}>
                        별점 재등록
                      </Button>
                      <Button variant="contained" br="round" onClick={() => moveToNextFulfill()}>
                        다음 평가
                      </Button>
                    </ButtonBox>
                  )}
                </InfoSection>
              </MainContentArea>

              {/* 공통 댓글 영역 */}
              <CommentWrap $isMobile={isMobile}>
                <CommentList>
                  <p className="title">댓글 {fulfillDetail.data.comment_count > 0 ? fulfillDetail.data.comment_count : ""}</p>
                  <List pd="0" itemPd="20px 0">
                    <CommentItem data={fulfillDetail.data.comments} onClick={clickCmtImages} />
                  </List>
                </CommentList>
                <FulFillCommentInputV2 />
              </CommentWrap>
            </>
          )}
        </ResponsiveContainer>
      </Body>

      <BottomModal modalTitle="이행글 관리" ref={processModalRef}>
        <List pd="0 15px">
          <FulfillProcessItem onClick={(v: string) => fulfillProcessBtn(v)} />
        </List>
      </BottomModal>

      <BottomModal modalTitle="사진 다운로드" ref={downloadModalRef}>
        <List pd="0 15px">
          <li>
            <DownloadOptionBtn type="button" onClick={downloadCurrentImage}>
              현재 사진 다운로드
            </DownloadOptionBtn>
          </li>
          <li>
            <DownloadOptionBtn type="button" onClick={downloadAllImages} disabled={isDownloadingAll}>
              {isDownloadingAll ? "다운로드 중..." : "전체 다운로드"}
            </DownloadOptionBtn>
          </li>
        </List>
      </BottomModal>

      <ImageModal ref={imageModalRef}>
        <ModalImgSlide>
          <StyledSlider {...setImgSlide2}>
            {fulfillDetail.isSuccess &&
              fulfillDetail.data.images.length > 0 &&
              fulfillDetail.data.images.map((v: any, k: number) => {
                return (
                  <div className="img-box" key={k}>
                    {v.ext === "mp4" || v.ext === "mov" ? (
                      <VideoBox controls playAllow vimeoId={v.img_url} />
                    ) : (
                      <img src={imageServerIp + v.img_url} alt={v.img_origin_name} />
                    )}
                  </div>
                );
              })}
          </StyledSlider>
        </ModalImgSlide>
      </ImageModal>

      <ImageModal ref={cmtImgModalRef}>
        <ModalImgSlide>
          <StyledSlider ref={(slider: Slider | null) => setImageSlider(slider)} {...settings}>
            {cmtView.cmtType === "parent"
              ? fulfillDetail?.data &&
                fulfillDetail?.data.comments &&
                fulfillDetail.data.comments.length > 0 &&
                fulfillDetail.data.comments[cmtView.cmtIdx].images.length > 0 &&
                fulfillDetail.data.comments[cmtView.cmtIdx].images.map((v: any, k: number) => {
                  return (
                    <div className="img-box" key={k}>
                      {v.ext === "mp4" || v.ext === "mov" ? (
                        <VideoBox
                          controls
                          playAllow
                          vimeoId={v.img_url}
                          // src={imageServerIp + v.img_url}
                        />
                      ) : (
                        <img src={imageServerIp + v.img_url} alt={v.img_origin_name} />
                      )}
                    </div>
                  );
                })
              : fulfillDetail?.data.comments &&
                fulfillDetail.data.comments.length > 0 &&
                fulfillDetail.data.comments[cmtView.cmtIdx].childComments[cmtView.cmtchildIdx].images.length > 0 &&
                fulfillDetail.data.comments[cmtView.cmtIdx].childComments[cmtView.cmtchildIdx].images.map((v: any, k: number) => {
                  return (
                    <div className="img-box" key={k}>
                      {v.ext === "mp4" || v.ext === "mov" ? (
                        <VideoBox
                          controls
                          playAllow
                          vimeoId={v.img_url}
                          // src={imageServerIp + v.img_url}
                        />
                      ) : (
                        <img src={imageServerIp + v.img_url} alt={v.img_origin_name} />
                      )}
                    </div>
                  );
                })}
          </StyledSlider>
        </ModalImgSlide>
      </ImageModal>

      <BottomModal
        modalTitle={fulfillDetail.isSuccess && fulfillDetail.data.score !== null ? "별점 재등록" : "별점 등록"}
        footerBtn="등록"
        ref={scoreModalRef}
        onClick2={setAssessScore}
      >
        {fulfillDetail.isSuccess && <ScoreRegisterBox star={fulfillDetail.data.score} value={fulfillDetail.data.assess_contents} />}
      </BottomModal>
    </>
  );
}

// PC/모바일 반응형 컨테이너
const ResponsiveContainer = styled.div<{ $isMobile: boolean }>`
  padding: ${({ $isMobile }) => ($isMobile ? "0" : "24px")};
`;

// 메인 콘텐츠 영역 (PC: 2컬럼, 모바일: 1컬럼)
const MainContentArea = styled.div<{ $isMobile: boolean }>`
  display: ${({ $isMobile }) => ($isMobile ? "block" : "flex")};
  gap: ${({ $isMobile }) => ($isMobile ? "0" : "32px")};
  align-items: stretch;
`;

// 이미지 섹션
const ImageSection = styled.div<{ $isMobile: boolean }>`
  ${({ $isMobile }) =>
    $isMobile
      ? `
    width: 100%;
  `
      : `
    flex: 1;
    min-width: 0;
  `}
`;

// 상세 정보 섹션
const InfoSection = styled.div<{ $isMobile: boolean }>`
  ${({ $isMobile }) =>
    $isMobile
      ? `
    width: 100%;
  `
      : `
    flex: 1;
    min-width: 0;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    padding: 24px;
  `}
`;

const BeforeConfirm = styled.div<{ $isMobile: boolean }>`
  > p {
    padding: 20px 0 52px;
  }
  border-bottom: 1px solid ${({ theme }) => theme.color.linePrimary};
  color: ${({ theme }) => theme.color.secondary};
  text-align: center;
`;

const BeforeAssess = styled.div<{ $isMobile: boolean }>`
  > p {
    padding: 52px 0;
  }
  border-top: 1px solid ${({ theme }) => theme.color.linePrimary};
  border-bottom: 1px solid ${({ theme }) => theme.color.linePrimary};
  color: ${({ theme }) => theme.color.secondary};
  text-align: center;
`;

const DownloadOptionBtn = styled.button`
  width: 100%;
  padding: 24px 0;
  border: none;
  background: none;
  color: ${({ theme }) => theme.color.primary};
  font-size: 16px;
  &:disabled {
    color: ${({ theme }) => theme.color.tertiary};
  }
`;

const ImgSlide = styled.div<{ $isMobile: boolean }>`
  position: relative;
  overflow: hidden;
  height: auto;
  background-color: #fff;
  > .img-download {
    z-index: 10;
    position: absolute;
    top: 24px;
    right: 15px;
    display: block;
    width: 32px;
    height: 32px;
    line-height: 30px;
    padding: 0;
    border: 1px solid ${({ theme }) => theme.color.linePrimary};
    border-radius: 16px;
    background-color: #fff;
    text-align: center;
    > svg {
      display: inline;
      position: relative;
      top: -2px;
    }
    cursor: pointer;
  }
  .tag-redeem {
    display: inline-block;
    padding: 4px 10px;
    position: absolute;
    top: 24px;
    left: 15px;
    background-color: ${({ theme }) => theme.color.main};
    color: #fff;
    font-size: 14px;
  }

  ${({ $isMobile }) =>
    !$isMobile &&
    css`
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    height: 100%;

    .img-box.detail-slide {
      height: 480px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #f5f5f5;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    ${pcDetailSliderArrowStyles}

    ${/* dots 위치 조정 및 스타일 - 이미지 위에 표시 */ ""}
    .slick-dots {
      bottom: 16px;
      z-index: 10;
    }

    .slick-dots li button:before {
      border: none;
      background-color: rgba(255, 255, 255, 0.5);
    }

    .slick-dots li.slick-active button:before {
      border: none;
      background-color: #fff;
    }

    ${/* 슬라이더 영역 이미지 꽉 차게 */ ""}
    .slick-slider,
    .slick-list,
    .slick-track,
    .slick-slide,
    .slick-slide > div {
      height: 100%;
    }

    .slick-slide > div > div {
      height: 100%;
    }
  `}
`;

const EditorContentsBox = styled.div<{ $isMobile: boolean }>`
  padding: ${({ $isMobile }) => ($isMobile ? "16px 15px 18px" : "0 0 18px")};
  & + div {
    padding-top: 32px;
    margin-top: 16px;
    border-top: 1px solid ${({ theme }) => theme.color.linePrimary};
  }
  & + .assess-btn-box {
    margin-top: 0;
    border-top: none;
  }
  > div {
    line-height: 20px;
    word-break: break-word;
    white-space: pre-wrap;
  }
  .date {
    margin-top: 16px;
    font-family: "Poppins";
    color: ${({ theme }) => theme.color.tertiary};
  }
`;

const ButtonBox = styled.div<{ $isMobile: boolean }>`
  ${({ theme }) => theme.flexBox.flexMixin()}
  padding: ${({ $isMobile }) => ($isMobile ? "15px 15px 52px" : "15px 0")};
  border-bottom: ${({ $isMobile, theme }) => ($isMobile ? `1px solid ${theme.color.linePrimary}` : "none")};
  > button {
    margin-right: 10px;
    &:last-child {
      margin-right: 0;
    }
  }
`;

const ModalImgSlide = styled.div`
  height: 100%;
  background: #ccc;
  ${pcModalBackgroundStyles}
`;

const StyledSlider = styled(Slider)`
  .slick-track {
    height: 100%;
  }
  .slick-slide {
    height: 100%;
    ${({ theme }) => theme.flexBox.flexMixin("column", "center", "center")}
    > div {
      position: relative;
      width: 100%;
      height: auto;
      overflow-y: scroll;
    }
    .img-box {
      padding-top: 0;
      text-align: center;
      > img {
        position: initial;
        top: 0;
        left: 0;
        transform: none;
      }
      > .video-wrap {
        position: initial;
        top: 0;
        left: 0;
        transform: none;
      }
    }
  }
  .slick-list {
    background: rgba(0, 0, 0, 0.5);
  }

  ${pcModalSliderStyles}
`;

const RedeemBox = styled.div<{ $isMobile: boolean }>`
  margin-bottom: 25px;
  > div {
    ${({ theme }) => theme.flexBox.flexMixin()}
    padding: ${({ $isMobile }) => ($isMobile ? "0 15px" : "0")};
    margin-bottom: 16px;
    .title {
      font-size: 16px;
      font-weight: 700;
    }
    .num {
      color: ${({ theme }) => theme.color.secondary};
    }
  }
  > ul {
    overflow: hidden;
    overflow-x: scroll;
    width: 100%;
    padding-left: ${({ $isMobile }) => ($isMobile ? "15px" : "0")};
    ${({ theme }) => theme.flexBox.flexMixin("row", "flex-start", "center")}
  }
`;

const StartCmtBox = styled.div<{ $isMobile: boolean }>`
  padding: ${({ $isMobile }) => ($isMobile ? "15px 15px 25px" : "15px 0 25px")};
  .title {
    margin-bottom: 16px;
    font-size: 16px;
    font-weight: 700;
  }
`;

const CommentWrap = styled.div<{ $isMobile: boolean }>`
  position: relative;
  flex-grow: 1;

  ${({ $isMobile }) =>
    !$isMobile &&
    `
    margin-top: 32px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  `}
`;

const CommentList = styled.div`
  /* min-height: calc(100% - 55px); */
  padding: 30px 15px;
  .title {
    font-size: 16px;
    padding-bottom: 15px;
    border-bottom: 1px solid ${({ theme }) => theme.color.linePrimary};
  }
  ul {
    overflow: hidden;
  }

  @media (min-width: 769px) {
    padding: 24px;

    .title {
      font-size: 18px;
      font-weight: 600;
    }
  }
`;

export default fulfillDetail;
